import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { createStyleSheet, globalStyles } from "../utils/styles";

import { DatePicker } from "@mui/x-date-pickers";
import TaskAddIcon from "@mui/icons-material/TaskOutlined";
import { useTranslation } from "react-i18next";

export enum AddFollowupTaskDialogField {
  title = "title",
  description = "description",
  due_date = "due_date",
  other = "other",
}

const AddFollowupTaskDialog = (props: {
  open: boolean;
  validateFields: (taskData: {
    title: string;
    description: string;
    dueDate: Date | null;
  }) => { field?: AddFollowupTaskDialogField; errorText?: string } | undefined;
  onClose: () => void;
  onSave: (taskData: {
    title: string;
    description: string;
    dueDate: Date | null;
  }) => void;
}) => {
  const { open, validateFields, onClose, onSave } = props;
  const { t } = useTranslation("AddFollowupTaskDialog");

  // Component state
  const [followUpTaskTitle, setFollowUpTaskTitle] = useState("");
  const [followUpTaskDescription, setFollowUpTaskDescription] = useState("");
  const [followUpTaskDueDate, setFollowUpTaskDueDate] = useState<Date | null>(
    null
  );
  const [invalidFieldInfo, setInvalidFieldInfo] = useState<{
    field?: AddFollowupTaskDialogField;
    errorText?: string;
  }>({});

  // Callbacks
  const handleFollowUpTaskTitleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (invalidFieldInfo.field === AddFollowupTaskDialogField.title) {
        setInvalidFieldInfo({});
      }
      setFollowUpTaskTitle(event.target.value);
    },
    [invalidFieldInfo.field]
  );
  const handleFollowUpTaskDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (invalidFieldInfo.field === AddFollowupTaskDialogField.description) {
        setInvalidFieldInfo({});
      }
      setFollowUpTaskDescription(event.target.value);
    },
    [invalidFieldInfo.field]
  );
  const handleFollowUpTaskDueDateChange = useCallback(
    (newDate: Date | null) => {
      if (invalidFieldInfo.field === AddFollowupTaskDialogField.due_date) {
        setInvalidFieldInfo({});
      }
      setFollowUpTaskDueDate(newDate);
    },
    [invalidFieldInfo.field]
  );
  const handleAddFollowUpTaskDialogSave = useCallback(async () => {
    const taskData = {
      title: followUpTaskTitle.trim(),
      description: followUpTaskDescription.trim(),
      dueDate: followUpTaskDueDate,
    };
    const result = validateFields(taskData);
    setInvalidFieldInfo({ field: result?.field, errorText: result?.errorText });
    if (typeof result?.field === "undefined") {
      onSave(taskData);
    }
  }, [
    followUpTaskTitle,
    followUpTaskDescription,
    followUpTaskDueDate,
    validateFields,
    onSave,
  ]);

  // Reactive code
  useEffect(() => {
    if (!open) {
      setFollowUpTaskTitle("");
      setFollowUpTaskDescription("");
      setFollowUpTaskDueDate(null);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>{t("addFollowUpTaskTitle")}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            ...styles.dialogContentRow,
            marginTop: 1,
            alignItems: "center",
          }}
        >
          <TaskAddIcon fontSize="large" color="primary" />
          <Box sx={styles.dialogContentRow}>
            <TextField
              label={t("followUpTaskTitle")}
              hiddenLabel={true}
              variant="outlined"
              value={followUpTaskTitle}
              onChange={handleFollowUpTaskTitleChange}
              error={
                invalidFieldInfo.field === AddFollowupTaskDialogField.title
              }
              helperText={
                invalidFieldInfo.field === AddFollowupTaskDialogField.title
                  ? invalidFieldInfo.errorText || t("invalidField")
                  : undefined
              }
              sx={styles.dialogTitleInput}
            />
            <DatePicker
              label={t("followUpTaskDueDate")}
              value={followUpTaskDueDate}
              onChange={handleFollowUpTaskDueDateChange}
              slotProps={{
                textField: {
                  error:
                    invalidFieldInfo.field ===
                    AddFollowupTaskDialogField.due_date,
                  helperText:
                    invalidFieldInfo.field ===
                    AddFollowupTaskDialogField.due_date
                      ? invalidFieldInfo.errorText || t("invalidField")
                      : undefined,
                },
              }}
              sx={styles.dialogDueDateInput}
            />
          </Box>
        </Box>
        <Box
          sx={{
            ...styles.dialogContentRow,
            marginTop: 2,
          }}
        >
          <TextField
            label={t("followUpTaskDescription")}
            variant="outlined"
            fullWidth
            multiline
            value={followUpTaskDescription}
            onChange={handleFollowUpTaskDescriptionChange}
            error={
              invalidFieldInfo.field === AddFollowupTaskDialogField.description
            }
            helperText={
              invalidFieldInfo.field === AddFollowupTaskDialogField.description
                ? invalidFieldInfo.errorText || t("invalidField")
                : undefined
            }
            sx={globalStyles.flexSpace}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={styles.dialogButtonContainer}>
        <Button onClick={onClose}>{t("common:cancel")}</Button>
        <Button onClick={handleAddFollowUpTaskDialogSave} autoFocus>
          {t("common:save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const styles = createStyleSheet({
  dialogContentRow: {
    display: "flex",
    flexDirection: "row",
  },
  dialogTitleInput: {
    marginLeft: 2,
    flex: 1,
  },
  dialogDueDateInput: {
    marginLeft: 1,
  },
  dialogButtonContainer: {
    px: 3,
    marginBottom: 1,
  },
});

export default AddFollowupTaskDialog;
